import { FC, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useBlocker } from "react-router-dom";

import { OrBoolean, RouteAction, RouteLocation } from "config/types";

import { defaultTo } from "lodash-es";

type BlockLocationState = {
  ignoreAction?: boolean;
};

type PromptCallback = (location: RouteLocation, action: RouteAction) => OrBoolean<void>;

type PromptEntry = {
  shouldRender: boolean;
  cb: PromptCallback;
};

// Module-level registry for all prompt instances.
// React Router 7 only supports a single useBlocker at a time, so we aggregate
// all usePrompt registrations here and check them from one shared blocker.
const promptRegistry = new Map<string, PromptEntry>();
let promptIdCounter = 0;

/**
 * Shared blocker component — render once inside the Router to manage the single
 * useBlocker instance that React Router 7 supports. All usePrompt hooks register
 * their callbacks with the module-level promptRegistry, and this component's
 * useBlocker checks all of them on each navigation attempt.
 */
export const SharedBlocker: FC = () => {
  const shouldBlock = useCallback(
    ({
      currentLocation,
      nextLocation,
      historyAction,
    }: {
      currentLocation: {
        pathname: string;
        search: string;
        hash: string;
        state: unknown;
        key: string;
      };
      nextLocation: {
        pathname: string;
        search: string;
        hash: string;
        state: unknown;
        key: string;
      };
      historyAction: string;
    }) => {
      const nextLocationState = (nextLocation.state as BlockLocationState) ?? {};
      const skipAction = defaultTo(nextLocationState.ignoreAction, false);
      if (skipAction) {
        return false;
      }

      for (const entry of Array.from(promptRegistry.values())) {
        if (!entry.shouldRender) {
          continue;
        }

        const unregister = defaultTo(
          entry.cb(currentLocation as RouteLocation, historyAction as RouteAction),
          false
        );
        // cb returns true to unregister (allow navigation), anything else blocks
        if (unregister !== true) {
          return true;
        }
      }
      return false;
    },
    []
  );

  const blocker = useBlocker(shouldBlock);

  // The shouldBlock callback fires synchronously before React Router commits
  // navigation. Registered callbacks (e.g. setJourneyExitModal) dispatch modals
  // during that synchronous check. Once shouldBlock returns true, the blocker
  // enters "blocked" state. We reset it in a layout effect (before paint) so
  // React Router releases the navigation hold before the modal's programmatic
  // navigate call can fire. Using useEffect (after paint) would cause the
  // navigate to be silently dropped while the blocker is still "blocked".
  useLayoutEffect(() => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  return null;
};

export const usePrompt = (shouldRender: boolean, cb: PromptCallback) => {
  const idRef = useRef(String(++promptIdCounter));

  // Update registry entry after every commit so the shared blocker reads the
  // latest shouldRender/cb on the next navigation attempt. Layout effect runs
  // synchronously before paint, matching the timing of the SharedBlocker reset.
  useLayoutEffect(() => {
    promptRegistry.set(idRef.current, { shouldRender, cb });
  });

  // Clean up on unmount
  useEffect(() => {
    const id = idRef.current;
    return () => {
      promptRegistry.delete(id);
    };
  }, []);
};
